/**
 * Server-Sent Events over fetch (the assistant POSTs with an Authorization header, which
 * EventSource cannot do). The server streams `data: {json}\n\n` blocks, each an
 * AssistantStreamEvent (generated from the OpenAPI document) whose `type` is
 * start | text | tool | ui | links | proposal | done | error.
 */
import type { AssistantEvent } from '@/lib/api/types';

export type { AssistantEvent };

const TYPES = new Set<string>(['start', 'text', 'tool', 'ui', 'links', 'proposal', 'done', 'error'] satisfies AssistantEvent['type'][]);

export interface SseParser {
  push: (chunk: string) => void;
  /** Handle a final block that was not terminated by a blank line. */
  flush: () => void;
}

/** Incremental parser: feed it decoded text in arbitrary chunks. */
export function createSseParser(onEvent: (event: AssistantEvent) => void): SseParser {
  let buffer = '';

  const handle = (block: string) => {
    const data = block
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).replace(/^ /, ''))
      .join('\n');
    if (!data) return; // comments (": keepalive"), event:/id:/retry: lines only
    try {
      const parsed = JSON.parse(data) as { type?: unknown };
      if (parsed && typeof parsed.type === 'string' && TYPES.has(parsed.type)) onEvent(parsed as AssistantEvent);
    } catch {
      // A malformed block is dropped; the stream continues.
    }
  };

  return {
    push(chunk) {
      buffer += chunk;
      // Normalise line endings, but keep a trailing CR: its LF may be in the next chunk.
      const keepCr = buffer.endsWith('\r');
      buffer = (keepCr ? buffer.slice(0, -1) : buffer).replace(/\r\n?/g, '\n') + (keepCr ? '\r' : '');
      let idx = buffer.indexOf('\n\n');
      while (idx >= 0) {
        handle(buffer.slice(0, idx));
        buffer = buffer.slice(idx + 2);
        idx = buffer.indexOf('\n\n');
      }
    },
    flush() {
      const rest = buffer.replace(/\r/g, '').trim();
      buffer = '';
      if (rest) handle(rest);
    },
  };
}

/** Read a streaming Response body to the end, calling `onEvent` per event. */
export async function readSse(response: Response, onEvent: (event: AssistantEvent) => void): Promise<void> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser(onEvent);
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true }));
  }
  parser.push(decoder.decode());
  parser.flush();
}
